import { useState, useRef, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '@/store/auth'
import { chatService, ShoppingLink } from '@/services/chat'
import {
  ArrowLeftOnRectangleIcon,
  PlusIcon,
  TrashIcon,
  Bars3Icon,
  XMarkIcon,
  ChatBubbleLeftRightIcon,
  ArrowTopRightOnSquareIcon,
  CpuChipIcon,
  ArrowRightIcon,
  Cog6ToothIcon
} from '@heroicons/react/24/outline'
import toast from 'react-hot-toast'
import FullScreenOnboarding from '@/components/FullScreenOnboarding'
import { DeviceInfoCollection } from '@/components/DeviceInfoCollection'
import { ConfettiEffect } from '@/components/ConfettiEffect'
import { MemoryModal } from '@/components/MemoryModal'
import { BeautifulMarkdown } from '@/components/BeautifulMarkdown'
import { useLocalAI } from '@/hooks/useLocalAI'
import { AIChatInput } from '@/components/ui/ai-chat-input'
import analytics from '@/utils/analytics'

const ease = [0.16, 1, 0.3, 1]

interface Message {
  id: string
  content: string
  role: 'user' | 'assistant'
  timestamp: Date
  isStreaming?: boolean
  elaborated?: boolean
  originalContent?: string
  shoppingLinks?: ShoppingLink[]
}

interface Chat {
  id: string
  title: string
  messages: Message[]
  createdAt: Date
  updatedAt: Date
}

// ============================================================================
// Shopping Links Component
// ============================================================================

const ShoppingLinks = ({ links }: { links: ShoppingLink[] }) => {
  if (!links?.length) return null

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease }}
      className="mt-6"
    >
      <span className="font-body text-[10px] tracking-[0.25em] text-[#c8a88a]/40 uppercase mb-4 block">
        Where to Buy
      </span>
      <div className="grid gap-2">
        {links.map((link, i) => (
          <motion.a
            key={i}
            href={link.url}
            target="_blank"
            rel="noopener noreferrer"
            initial={{ opacity: 0, x: -10 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: i * 0.05, ease }}
            className="flex items-center justify-between p-4 border border-[#eee8dc]/[0.06] hover:border-[#c8a88a]/20 transition-all group"
          >
            <span className="font-body text-sm text-[#eee8dc]/70 group-hover:text-[#c8a88a] transition-colors font-medium">
              {link.store || 'Store'}
            </span>
            <div className="flex items-center gap-4">
              {link.price && (
                <span className="font-body text-sm font-semibold text-[#c8a88a]">{link.price}</span>
              )}
              <ArrowTopRightOnSquareIcon className="w-4 h-4 text-[#eee8dc]/20 group-hover:text-[#c8a88a] transition-colors" />
            </div>
          </motion.a>
        ))}
      </div>
    </motion.div>
  )
}

// ============================================================================
// Message Bubble Component - PREMIUM DESIGN
// ============================================================================

const MessageBubble = ({
  message,
  isUser,
  onElaborate
}: {
  message: Message
  isUser: boolean
  onElaborate?: (id: string, query: string) => void
}) => {
  const [elaborating, setElaborating] = useState(false)

  if (!message.content?.trim() && !message.isStreaming) return null

  const handleElaborate = async () => {
    if (!onElaborate) return
    setElaborating(true)
    try {
      await onElaborate(message.id, 'Please elaborate')
    } finally {
      setElaborating(false)
    }
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, ease }}
      className={`flex ${isUser ? 'justify-end' : 'justify-start'} mb-8`}
    >
      <div className={`w-full ${isUser ? 'max-w-[85%] md:max-w-[70%]' : 'max-w-full'}`}>

        {/* Label */}
        <div className={`flex items-center gap-3 mb-2.5 ${isUser ? 'flex-row-reverse' : ''}`}>
          <span className="font-body text-[11px] tracking-[0.15em] text-[#eee8dc]/25 uppercase">
            {isUser ? 'You' : 'Jpdz'}
          </span>
          {!isUser && message.isStreaming && (
            <motion.div
              animate={{ opacity: [0.4, 1, 0.4] }}
              transition={{ duration: 1.5, repeat: Infinity }}
              className="w-1.5 h-1.5 bg-[#6e1f1b] rounded-full"
            />
          )}
        </div>

        {/* Message Content */}
        <div className={`relative overflow-hidden rounded-2xl transition-all duration-300 ${isUser
          ? 'bg-[#eee8dc]/[0.04] border border-[#eee8dc]/[0.08]'
          : ''
          }`}
        >
          {isUser && (
            <div className="absolute top-0 right-0 w-[2px] h-full bg-[#6e1f1b]/40" />
          )}

          {!isUser && (
            <div className="absolute top-0 left-0 w-[2px] h-full bg-gradient-to-b from-[#c8a88a]/30 to-transparent" />
          )}

          {message.isStreaming && !message.content?.trim() ? (
            <div className="p-6 pl-6 flex items-center gap-3">
              <div className="flex gap-1.5">
                {[0, 1, 2].map((i) => (
                  <motion.span
                    key={i}
                    className="w-1.5 h-1.5 bg-[#c8a88a]/50 rounded-full"
                    animate={{ opacity: [0.3, 1, 0.3] }}
                    transition={{ duration: 1, repeat: Infinity, delay: i * 0.15 }}
                  />
                ))}
              </div>
              <span className="font-body text-[12px] text-[#eee8dc]/20 tracking-wider">Thinking...</span>
            </div>
          ) : (
            <div className={`p-5 ${isUser ? 'pr-6' : 'pl-5'}`}>
              {isUser ? (
                <p className="font-body text-[15px] leading-[1.7] whitespace-pre-wrap text-[#eee8dc]/80">
                  {message.content}
                </p>
              ) : (
                <div className="font-body text-[15px] leading-[1.75] text-[#eee8dc]/55 prose-invert max-w-none">
                  <BeautifulMarkdown content={message.content} />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Shopping Links */}
        {!isUser && message.shoppingLinks && message.shoppingLinks.length > 0 && (
          <ShoppingLinks links={message.shoppingLinks} />
        )}

        {/* Elaborate Button */}
        {!isUser && onElaborate && !message.elaborated && !message.isStreaming && (
          <motion.div
            initial={{ opacity: 0, y: -5 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="flex justify-start mt-3 ml-1"
          >
            <button
              onClick={handleElaborate}
              disabled={elaborating}
              className="group flex items-center gap-2 px-3 py-1.5 hover:bg-[#eee8dc]/[0.03] transition-all disabled:opacity-50"
            >
              <div className="w-4 h-4 rounded-full border border-[#eee8dc]/10 flex items-center justify-center group-hover:border-[#c8a88a]/30 transition-colors">
                <PlusIcon className="w-2.5 h-2.5 text-[#eee8dc]/20 group-hover:text-[#c8a88a]" />
              </div>
              <span className="font-body text-[11px] text-[#eee8dc]/20 group-hover:text-[#eee8dc]/50 transition-colors">
                {elaborating ? 'Elaborating...' : 'Dig deeper'}
              </span>
            </button>
          </motion.div>
        )}
      </div>
    </motion.div>
  )
}

// ============================================================================
// Quick Suggestion Chips
// ============================================================================

const suggestions = [
  "Best laptop for coding?",
  "iPhone vs Android?",
  "Noise-canceling earbuds?",
  "4K monitor recommendations"
]

const SuggestionChips = ({ onSelect }: { onSelect: (text: string) => void }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay: 0.4, ease }}
    className="flex flex-wrap gap-3 justify-center mt-10"
  >
    {suggestions.map((text, i) => (
      <motion.button
        key={i}
        onClick={() => onSelect(text)}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5 + i * 0.08, ease }}
        className="px-5 py-2.5 rounded-xl border border-[#eee8dc]/[0.06] hover:border-[#c8a88a]/20 text-[#eee8dc]/30 hover:text-[#eee8dc]/60 font-body text-[13px] transition-all duration-300"
      >
        {text}
      </motion.button>
    ))}
  </motion.div>
)

// ============================================================================
// Main Chat Page
// ============================================================================

export function ChatPage() {
  const navigate = useNavigate()
  const { user, logout, token, isInitializing } = useAuthStore()

  const [input, setInput] = useState('')
  const [sidebarOpen, setSidebarOpen] = useState(true)
  const [selectedChatId, setSelectedChatId] = useState<string | null>(null)
  const [chats, setChats] = useState<Chat[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isSending, setIsSending] = useState(false)
  const [isStreaming, setIsStreaming] = useState(false)
  const [showOnboarding, setShowOnboarding] = useState(false)
  const [showDeviceInfo, setShowDeviceInfo] = useState(false)
  const [showConfetti, setShowConfetti] = useState(false)
  const [showMemoryModal, setShowMemoryModal] = useState(false)
  const [userDeviceInfo, setUserDeviceInfo] = useState<{
    phone?: string; laptop?: string; earphones?: string; wearable?: string;
  } | null>(null)

  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  const { mode: aiMode, generate: generateWithBackend, initializeWebLLM } = useLocalAI()

  const currentChat = chats.find(c => c.id === selectedChatId)

  // Auth guard — redirect to login if not authenticated
  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const hasTokenInUrl = params.has('token')

    if (isInitializing || hasTokenInUrl) return
    if ((window as any).__JPDZ_AUTH_REDIRECT_DONE__) return

    if (!user || !token) {
      (window as any).__JPDZ_AUTH_REDIRECT_DONE__ = true
      navigate('/login')
    }
  }, [user, token, isInitializing, navigate])

  useEffect(() => { loadChats(); loadUserDeviceInfo() }, [])

  useEffect(() => {
    if (aiMode === 'backend') initializeWebLLM().catch(console.error)
  }, [aiMode, initializeWebLLM])

  useEffect(() => {
    if (user && token) {
      const hasCompleted = localStorage.getItem('jpdz_onboarding_completed')
      const isFirstTime = localStorage.getItem('jpdz_first_time_user') === 'true'
      const urlWelcome = new URLSearchParams(window.location.search).get('welcome') === 'true'

      if (!hasCompleted && (isFirstTime || urlWelcome)) {
        if (urlWelcome && !isFirstTime) localStorage.setItem('jpdz_first_time_user', 'true')
        setTimeout(() => setShowOnboarding(true), 1000)
      }
      if (isFirstTime || urlWelcome) localStorage.removeItem('jpdz_first_time_user')
    }
  }, [user, token])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [currentChat?.messages])

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.style.height = 'auto'
      inputRef.current.style.height = `${Math.min(inputRef.current.scrollHeight, 150)}px`
    }
  }, [input])

  // Data Loading
  const CACHE_KEY = 'jpdz_chats_cache'

  const loadChats = async () => {
    if ((window as any).__JPDZ_CHATS_FETCHED__) {
      const cached = localStorage.getItem(CACHE_KEY)
      if (cached) {
        setChats(JSON.parse(cached).map((c: any) => ({
          ...c,
          messages: c.messages?.map((m: any) => ({ ...m, timestamp: new Date(m.timestamp) })) || [],
          createdAt: new Date(c.createdAt),
          updatedAt: new Date(c.updatedAt)
        })))
      }
      return
    }

    (window as any).__JPDZ_CHATS_FETCHED__ = true
    setIsLoading(true)

    try {
      const history = await chatService.getChats()
      const safe = history.map(c => ({
        ...c,
        messages: c.messages?.map(m => ({ ...m, timestamp: new Date(m.timestamp || Date.now()) })) || [],
        createdAt: new Date(c.created_at || Date.now()),
        updatedAt: new Date(c.created_at || Date.now())
      }))
      setChats(safe)
      localStorage.setItem(CACHE_KEY, JSON.stringify(safe))
    } catch (err: any) {
      if (err.response?.status === 401) navigate('/login')
    } finally {
      setIsLoading(false)
    }
  }

  const loadUserDeviceInfo = async () => {
    try {
      const t = token || localStorage.getItem('jpdz_token')
      if (!t) return
      const res = await fetch('/api/device/get-device-info', {
        headers: { Authorization: `Bearer ${t}` },
        credentials: 'include'
      })
      if (res.ok) {
        const data = await res.json()
        if (data.device_info) setUserDeviceInfo(data.device_info)
      }
    } catch { }
  }

  // Actions
  const createNewChat = async () => {
    try {
      const newChat = await chatService.createChat()
      const safe = { ...newChat, messages: newChat.messages?.map(m => ({ ...m, timestamp: new Date(m.timestamp || Date.now()) })) || [] }
      setChats(prev => [safe, ...prev])
      setSelectedChatId(safe.id)
      toast.success('New chat created')
    } catch (err: any) {
      if (err.response?.status === 401) navigate('/login')
      else toast.error('Failed to create chat')
    }
  }

  const selectChat = async (chatId: string) => {
    setSelectedChatId(chatId)
    const existing = chats.find(c => c.id === chatId)
    if (existing?.messages?.length) return

    try {
      const msgs = await chatService.getChatMessages(chatId)
      setChats(prev => prev.map(c =>
        c.id === chatId ? { ...c, messages: msgs.map(m => ({ ...m, timestamp: new Date(m.timestamp || Date.now()) })) } : c
      ))
    } catch { }
  }

  const deleteChat = async (chatId: string) => {
    try {
      await chatService.deleteChat(undefined, chatId)
      setChats(prev => prev.filter(c => c.id !== chatId))
      if (selectedChatId === chatId) setSelectedChatId(null)
      toast.success('Chat deleted')
    } catch (err: any) {
      if (err.response?.status === 401) navigate('/login')
      else toast.error('Failed to delete')
    }
  }

  const sendMessage = async (messageText?: string) => {
    const text = messageText || input.trim()
    if (!text || isSending || isStreaming) return

    setInput('')
    setIsSending(true)
    analytics.messageSent(selectedChatId || undefined)

    try {
      let chatId = selectedChatId
      if (!chatId) {
        const newChat = await chatService.createChat()
        chatId = newChat.id
        setChats(prev => [{ ...newChat, messages: [] }, ...prev])
        setSelectedChatId(chatId)
      }

      const userMsg: Message = {
        id: `temp-user-${Date.now()}`,
        content: text,
        role: 'user',
        timestamp: new Date()
      }

      setChats(prev => prev.map(c =>
        c.id === chatId ? { ...c, messages: [...c.messages, userMsg] } : c
      ))

      const assistantId = `temp-assistant-${Date.now()}`
      const assistantMsg: Message = {
        id: assistantId,
        content: '',
        role: 'assistant',
        timestamp: new Date(),
        isStreaming: true
      }

      setChats(prev => prev.map(c =>
        c.id === chatId ? { ...c, messages: [...c.messages, assistantMsg] } : c
      ))

      setIsSending(false)
      setIsStreaming(true)

      const chatHistory = currentChat?.messages.slice(-10).map(m => ({
        role: m.role as 'user' | 'assistant',
        content: m.content
      })) || []

      for await (const chunk of generateWithBackend(text, chatHistory, chatId)) {
        const chunkText = typeof chunk === 'string' ? chunk : chunk?.text || chunk?.content || ''
        if (chunkText) {
          setChats(prev => prev.map(c =>
            c.id === chatId
              ? {
                ...c,
                messages: c.messages.map(m =>
                  m.id === assistantId ? { ...m, content: m.content + chunkText } : m
                )
              }
              : c
          ))
        }
      }

      setChats(prev => prev.map(c =>
        c.id === chatId
          ? { ...c, messages: c.messages.map(m => m.id === assistantId ? { ...m, isStreaming: false } : m) }
          : c
      ))

    } catch (error: any) {
      toast.error('Failed to send message')
    } finally {
      setIsSending(false)
      setIsStreaming(false)
    }
  }

  const handleElaborate = async (messageId: string, query: string) => {
    if (!selectedChatId || isStreaming) return

    setIsStreaming(true)

    try {
      const originalMessage = currentChat?.messages.find(m => m.id === messageId)
      if (!originalMessage) return

      setChats(prev => prev.map(c =>
        c.id === selectedChatId
          ? {
            ...c, messages: c.messages.map(m =>
              m.id === messageId
                ? { ...m, elaborated: true, originalContent: m.content, isStreaming: true, content: '' }
                : m
            )
          }
          : c
      ))

      const elaborateHistory = currentChat?.messages.slice(-6).map(m => ({
        role: m.role as 'user' | 'assistant',
        content: m.content
      })) || []

      for await (const chunk of generateWithBackend(
        `Please elaborate on your previous response about: "${originalMessage.content.substring(0, 100)}..."`,
        elaborateHistory,
        selectedChatId
      )) {
        const chunkText = typeof chunk === 'string' ? chunk : chunk?.text || chunk?.content || ''
        if (chunkText) {
          setChats(prev => prev.map(c =>
            c.id === selectedChatId
              ? { ...c, messages: c.messages.map(m => m.id === messageId ? { ...m, content: m.content + chunkText } : m) }
              : c
          ))
        }
      }

      setChats(prev => prev.map(c =>
        c.id === selectedChatId
          ? { ...c, messages: c.messages.map(m => m.id === messageId ? { ...m, isStreaming: false } : m) }
          : c
      ))

    } catch {
      toast.error('Failed to elaborate')
    } finally {
      setIsStreaming(false)
    }
  }

  const handleLogout = () => { logout(); navigate('/') }

  const handleOnboardingComplete = () => { setShowOnboarding(false); setShowDeviceInfo(true) }

  const handleDeviceInfoComplete = async () => {
    setShowDeviceInfo(false)
    await loadUserDeviceInfo()
    setShowConfetti(true)
    if (token && !chats.length) await createNewChat()
    toast.success('Welcome to JPDZ!')
    setTimeout(() => setShowConfetti(false), 5000)
  }

  const handleDeviceInfoSkip = async () => {
    setShowDeviceInfo(false)
    setShowConfetti(true)
    if (token && !chats.length) await createNewChat()
    toast.success('Welcome!')
    setTimeout(() => setShowConfetti(false), 4000)
  }

  // ======================================================================
  // Render
  // ======================================================================

  return (
    <div className="flex h-screen overflow-hidden bg-[#211d18]">

      {/* Subtle grid pattern */}
      <div className="fixed inset-0 pointer-events-none opacity-20" style={{ backgroundImage: 'linear-gradient(90deg, rgba(238,232,220,0.02) 1px, transparent 1px), linear-gradient(rgba(238,232,220,0.015) 1px, transparent 1px)', backgroundSize: '64px 64px' }} />

      {/* Mobile Backdrop */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setSidebarOpen(false)}
            className="fixed inset-0 bg-[#211d18]/80 backdrop-blur-sm z-20 lg:hidden"
          />
        )}
      </AnimatePresence>

      {/* Sidebar */}
      <motion.aside
        initial={false}
        animate={{ width: sidebarOpen ? 320 : 0 }}
        transition={{ duration: 0.4, ease }}
        className="relative z-30 flex-shrink-0 overflow-hidden bg-[#1a1614] border-r border-[#eee8dc]/[0.04]"
      >
        <div className="flex flex-col h-full w-80">
          {/* Header */}
          <div className="relative p-5 border-b border-[#eee8dc]/[0.04]">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                <span className="font-serif text-xl text-[#eee8dc]">Jpdz</span>
                <span className="font-body text-[10px] tracking-[0.2em] text-[#eee8dc]/20 uppercase">Chat</span>
              </div>
              <button onClick={() => setSidebarOpen(false)} className="p-2 rounded-lg hover:bg-[#eee8dc]/[0.04] transition-colors">
                <XMarkIcon className="w-4 h-4 text-[#eee8dc]/25" />
              </button>
            </div>

            <motion.button
              onClick={createNewChat}
              whileTap={{ scale: 0.98 }}
              className="w-full flex items-center justify-center gap-3 px-5 py-3 rounded-xl bg-[#6e1f1b] text-[#eee8dc] font-body text-[13px] font-medium transition-colors hover:bg-[#8a2822]"
            >
              <PlusIcon className="w-4 h-4" />
              <span>New Chat</span>
            </motion.button>
          </div>

          {/* Chat List */}
          <div className="flex-1 overflow-y-auto p-3">
            <span className="font-body text-[10px] tracking-[0.2em] text-[#eee8dc]/15 uppercase block mb-3 px-3">
              Recent
            </span>

            {isLoading ? (
              <div className="space-y-2">
                {[...Array(4)].map((_, i) => (
                  <div key={i} className="h-12 rounded-lg bg-[#eee8dc]/[0.02] animate-pulse" />
                ))}
              </div>
            ) : chats.length === 0 ? (
              <div className="text-center py-16 px-4">
                <ChatBubbleLeftRightIcon className="w-8 h-8 text-[#eee8dc]/10 mx-auto mb-4" />
                <span className="font-body text-[13px] text-[#eee8dc]/20 block">No chats yet</span>
              </div>
            ) : (
              <div className="space-y-1">
                {chats.map((chat, idx) => (
                  <motion.div
                    key={chat.id}
                    onClick={() => selectChat(chat.id)}
                    initial={{ opacity: 0, x: -10 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: idx * 0.03 }}
                    className={`group cursor-pointer p-3 rounded-lg transition-all duration-200 ${selectedChatId === chat.id
                      ? 'bg-[#eee8dc]/[0.05]'
                      : 'hover:bg-[#eee8dc]/[0.02]'
                      }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <p className={`font-body text-[13px] truncate ${selectedChatId === chat.id ? 'text-[#eee8dc]' : 'text-[#eee8dc]/45'
                          }`}>{chat.title}</p>
                        <span className="font-body text-[10px] text-[#eee8dc]/15 mt-1 block">
                          {chat.messages?.length || 0} messages
                        </span>
                      </div>
                      <button
                        onClick={(e) => { e.stopPropagation(); deleteChat(chat.id) }}
                        className="opacity-0 group-hover:opacity-100 p-1.5 rounded-md hover:bg-[#6e1f1b]/10 transition-all"
                      >
                        <TrashIcon className="w-3.5 h-3.5 text-[#eee8dc]/15 hover:text-[#6e1f1b]" />
                      </button>
                    </div>
                  </motion.div>
                ))}
              </div>
            )}
          </div>

          {/* User Profile */}
          <div className="p-3 border-t border-[#eee8dc]/[0.04]">
            <div className="flex items-center justify-between p-3 rounded-lg">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-8 h-8 rounded-lg bg-[#6e1f1b] flex items-center justify-center flex-shrink-0">
                  <span className="text-[#eee8dc] text-[11px] font-serif font-medium">
                    {user?.username?.[0]?.toUpperCase() || 'U'}
                  </span>
                </div>
                <div className="min-w-0">
                  <p className="font-body text-[13px] text-[#eee8dc]/55 truncate">{user?.username}</p>
                  <p className="font-body text-[10px] text-[#eee8dc]/15 truncate">{user?.email}</p>
                </div>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => setShowMemoryModal(true)} className="p-2 rounded-lg hover:bg-[#eee8dc]/[0.04] transition-colors">
                  <Cog6ToothIcon className="w-4 h-4 text-[#eee8dc]/20 hover:text-[#eee8dc]/40" />
                </button>
                <button onClick={handleLogout} className="p-2 rounded-lg hover:bg-[#6e1f1b]/10 transition-colors">
                  <ArrowLeftOnRectangleIcon className="w-4 h-4 text-[#eee8dc]/20 hover:text-[#6e1f1b]" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </motion.aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col relative min-w-0">

        {/* Header */}
        <motion.header
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative flex items-center gap-4 px-6 py-4 border-b border-[#eee8dc]/[0.04] bg-[#211d18]/90 backdrop-blur-xl"
        >
          <button
            onClick={() => setSidebarOpen(!sidebarOpen)}
            className="p-2 rounded-lg hover:bg-[#eee8dc]/[0.04] transition-colors"
          >
            <Bars3Icon className="w-5 h-5 text-[#eee8dc]/30" />
          </button>

          <div className="flex items-center gap-4 flex-1">
            <div>
              <h1 className="font-serif text-[17px] text-[#eee8dc]">
                {currentChat?.title || 'Jpdz'}
              </h1>
              <div className="flex items-center gap-4 mt-0.5">
                {userDeviceInfo && Object.values(userDeviceInfo).some(Boolean) && (
                  <span className="font-body text-[10px] tracking-[0.15em] text-[#c8a88a]/40 flex items-center gap-1.5 uppercase">
                    <CpuChipIcon className="w-3 h-3" />
                    Personalized
                  </span>
                )}
                {isStreaming && (
                  <motion.span
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="font-body text-[10px] tracking-[0.15em] text-[#eee8dc]/20 flex items-center gap-1.5"
                  >
                    <motion.span
                      className="w-1.5 h-1.5 bg-[#6e1f1b] rounded-full"
                      animate={{ opacity: [1, 0.3, 1] }}
                      transition={{ duration: 1, repeat: Infinity }}
                    />
                    Generating
                  </motion.span>
                )}
              </div>
            </div>
          </div>
        </motion.header>

        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto">
          {!currentChat ? (
            /* Welcome State */
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8 }}
              className="flex flex-col items-center justify-center h-full px-8 text-center relative"
            >
              <div className="max-w-2xl relative z-10">
                <motion.h2
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="font-serif text-[clamp(3rem,6vw,5.5rem)] font-medium text-[#eee8dc] mb-5 leading-[0.88]"
                >
                  What do you<br />
                  <span className="italic text-[#c8a88a]">need?</span>
                </motion.h2>

                <motion.p
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.35 }}
                  className="font-body text-[16px] text-[#eee8dc]/25 mb-12 max-w-md mx-auto leading-[1.8]"
                >
                  Ask about any tech product. Get honest, unbiased recommendations tailored to you.
                </motion.p>

                <motion.div
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.45 }}
                  className="flex flex-col sm:flex-row gap-4 justify-center"
                >
                  <motion.button
                    onClick={createNewChat}
                    whileTap={{ scale: 0.98 }}
                    className="group inline-flex items-center justify-center gap-3 rounded-xl px-8 py-4 bg-[#6e1f1b] text-[#eee8dc] font-body text-[14px] font-medium transition-colors hover:bg-[#8a2822]"
                  >
                    <span>Start Chat</span>
                    <ArrowRightIcon className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
                  </motion.button>
                  <motion.button
                    onClick={() => setShowMemoryModal(true)}
                    whileTap={{ scale: 0.98 }}
                    className="inline-flex items-center justify-center gap-3 rounded-xl px-8 py-4 border border-[#eee8dc]/[0.08] text-[#eee8dc]/40 font-body text-[14px] hover:border-[#eee8dc]/15 hover:text-[#eee8dc]/60 transition-all"
                  >
                    <CpuChipIcon className="w-4 h-4" />
                    <span>My Tech</span>
                  </motion.button>
                </motion.div>

                <SuggestionChips onSelect={(text) => { setInput(text); inputRef.current?.focus() }} />
              </div>
            </motion.div>
          ) : (
            /* Chat Messages */
            <div className="max-w-3xl mx-auto px-6 py-10 space-y-6">
              {currentChat.messages.map((msg) => (
                <MessageBubble
                  key={msg.id}
                  message={msg}
                  isUser={msg.role === 'user'}
                  onElaborate={handleElaborate}
                />
              ))}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* Input Area */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="relative px-6 py-5"
        >
          <AIChatInput
            placeholders={[
              "Ask about any tech product...",
              "Compare laptops, phones, headphones...",
              "Get personalized recommendations...",
              "What would you like to know?",
            ]}
            value={input}
            onChange={setInput}
            onSubmit={(val) => sendMessage(val)}
            disabled={isSending || isStreaming}
          />
        </motion.div>
      </div>

      {/* Modals */}
      {showOnboarding && <FullScreenOnboarding onComplete={handleOnboardingComplete} />}
      {showDeviceInfo && <DeviceInfoCollection onComplete={handleDeviceInfoComplete} onSkip={handleDeviceInfoSkip} />}
      <MemoryModal isOpen={showMemoryModal} onClose={() => { setShowMemoryModal(false); loadUserDeviceInfo() }} />
      <ConfettiEffect active={showConfetti} />
    </div>
  )
}